import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import client from '../client';
import { metaSchema, parseEnvelope } from '../envelope';

// Legacy fixture/UI types remain exported until the root-owned InboxPage seam
// consumes the two queue states below. They are not used to parse either live
// queue, and their old suggestion confidence is never rendered by S2 UI.
export const confidenceLevelSchema = z.enum(['high', 'med', 'low', 'user']);
export type ConfidenceLevel = z.infer<typeof confidenceLevelSchema>;

export const inboxSuggestionSchema = z.object({
  path: z.array(z.string()),
  confidence: confidenceLevelSchema,
  source: z.enum(['user', 'rule', 'ai']),
});
export type InboxSuggestion = z.infer<typeof inboxSuggestionSchema>;

export const inboxUncategorizedItemSchema = z.object({
  kind: z.literal('uncategorized'),
  transaction_id: z.string(),
  merchant_display: z.string(),
  merchant_raw: z.string(),
  merchant_substituted: z.boolean(),
  account_last4: z.string().nullable(),
  posted_at: z.string(),
  pending: z.boolean(),
  amount: z.string(),
  currency: z.string(),
  suggestion: inboxSuggestionSchema.nullable(),
  business_ambiguous: z.boolean(),
  business_hint: z.enum(['none', 'mixed', 'primary']).nullable(),
  note: z.string().nullable(),
}).strict();
export type InboxUncategorizedItem = z.infer<typeof inboxUncategorizedItemSchema>;

export const inboxTransferReviewItemSchema = z.object({
  kind: z.literal('transfer_review'),
  group_id: z.string(),
  link_kind: z.enum(['transfer', 'cc_payment']),
  leg_a: z.object({ account_last4: z.string().nullable(), amount: z.string(), posted_at: z.string() }).strict(),
  leg_b: z.object({ account_last4: z.string().nullable(), amount: z.string(), posted_at: z.string() }).strict(),
}).strict();
export type InboxTransferReviewItem = z.infer<typeof inboxTransferReviewItemSchema>;

export const inboxItemSchema = z.discriminatedUnion('kind', [
  inboxUncategorizedItemSchema,
  inboxTransferReviewItemSchema,
]);
export type InboxItem = z.infer<typeof inboxItemSchema>;

export const inboxMetaSchema = metaSchema.and(z.object({
  uncategorized_count: z.number().nullable().optional(),
  transfer_review_count: z.number().nullable().optional(),
}));
export type InboxMeta = z.infer<typeof inboxMetaSchema>;

const uuidSchema = z.string().uuid();
const moneySchema = z.string().regex(/^-?(0|[1-9][0-9]{0,14})\.[0-9]{4}$/);

/** Exact public line-item projection returned by GET /api/line-items?uncategorized=true. */
export const inboxUncategorizedLineItemSchema = z.object({
  line_item_id: uuidSchema,
  transaction_id: uuidSchema,
  account_id: uuidSchema,
  posted_at: z.string().datetime({ offset: true }),
  amount: moneySchema,
  currency: z.string().regex(/^[A-Z]{3}$/),
  merchant_id: uuidSchema.nullable(),
  merchant_display: z.string().nullable(),
  memo_raw: z.string().nullable(),
  category_id: uuidSchema.nullable(),
  category_slug: z.string().nullable(),
  is_business: z.boolean(),
  tags: z.array(z.string()),
  source: z.string(),
  version: z.number().int().positive(),
}).strict();
export type InboxUncategorizedLineItem = z.infer<typeof inboxUncategorizedLineItemSchema>;

export const inboxQueueMetaSchema = z.object({
  next_cursor: z.string().nullable(),
  total_count: z.number().int().nonnegative().nullable(),
  coverage: z.enum(['complete', 'partial']),
}).strict().superRefine((meta, context) => {
  if (meta.coverage === 'complete' && meta.total_count === null) {
    context.addIssue({ code: 'custom', path: ['total_count'], message: 'complete coverage requires total_count' });
  }
  if (meta.coverage === 'partial' && meta.total_count !== null) {
    context.addIssue({ code: 'custom', path: ['total_count'], message: 'partial coverage requires null total_count' });
  }
});
export type InboxQueueMeta = z.infer<typeof inboxQueueMetaSchema>;

const candidateLegInputSchema = z.object({
  transaction_id: uuidSchema,
  account: z.object({ last4: z.string().nullable() }).passthrough(),
  posted_at: z.string(),
  amount: moneySchema,
}).passthrough();

const candidateLegSchema = candidateLegInputSchema.transform((leg) => ({
  account_last4: leg.account.last4,
  amount: leg.amount,
  posted_at: leg.posted_at,
}));

/** Candidate queue item, retaining only the safe review identity and display legs. */
export const inboxCandidateSchema = z.object({
  candidate_id: z.string().min(1),
  evidence_token: z.string().min(1),
  kind_suggestion: z.enum(['transfer', 'cc_payment']),
  legs: z.tuple([candidateLegSchema, candidateLegSchema]),
}).strict().transform((candidate) => ({
  kind: 'transfer_review' as const,
  candidate_id: candidate.candidate_id,
  evidence_token: candidate.evidence_token,
  link_kind: candidate.kind_suggestion,
  leg_a: candidate.legs[0],
  leg_b: candidate.legs[1],
}));
export type InboxCandidateReviewItem = z.infer<typeof inboxCandidateSchema>;

export interface InboxQueueResult<T> {
  items: T[];
  meta: InboxQueueMeta;
}

/** Compatibility shape for consumers awaiting the serial B7 integration. */
export interface InboxItemsResult {
  items: InboxItem[];
  meta: InboxMeta | undefined;
}

function parseQueueResponse<T extends z.ZodTypeAny>(schema: T, payload: unknown): InboxQueueResult<z.infer<T>> {
  const parsed = parseEnvelope(z.array(schema), payload);
  return { items: parsed.data, meta: inboxQueueMetaSchema.parse(parsed.meta ?? {}) };
}

export class InboxUnavailableError extends Error {
  constructor() {
    super('Inbox review is not available yet.');
    this.name = 'InboxUnavailableError';
  }
}

export function useInboxItems(enabled = true) {
  const uncategorized = useQuery({
    queryKey: ['inbox', 'uncategorized'],
    enabled,
    retry: false,
    queryFn: async () => {
      const response = await client.get('/api/line-items', { params: { uncategorized: true, limit: 50 } });
      return parseQueueResponse(inboxUncategorizedLineItemSchema, response.data);
    },
  });
  const candidates = useQuery({
    queryKey: ['inbox', 'candidates'],
    enabled,
    retry: false,
    queryFn: async () => {
      const response = await client.get('/api/transactions/link-candidates', { params: { limit: 50 } });
      return parseQueueResponse(inboxCandidateSchema, response.data);
    },
  });

  return {
    // B7 consumes these independently; no aggregate query masks a failed queue.
    uncategorized,
    candidates,
    // Legacy aggregate is deliberately absent until B7 owns the composition.
    data: undefined as InboxItemsResult | undefined,
    isLoading: uncategorized.isLoading || candidates.isLoading,
    isError: uncategorized.isError || candidates.isError,
    error: uncategorized.error ?? candidates.error ?? null,
  };
}

